import React, { FunctionComponent, ReactNode } from 'react';
import Avatar from '@justgiving/avatar';
import { Totaliser } from '@justgiving/totaliser';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import parseCurrency from '../../utils/parseCurrency';

import styles from './BenefactorListItem.scss';
import { PageStatus } from '../../../types/__generated__';

type BenefactorListItemProps = {
  title: string | ReactNode;
  image?: string | null;
  currencyCode: string;
  amount: number;
  totaliserValue: number | null;
  supporterCount: number;
  url?: string;
  pageStatus: PageStatus;
};

const BenefactorListItem: FunctionComponent<BenefactorListItemProps> = ({
  title,
  image,
  currencyCode,
  amount,
  totaliserValue,
  supporterCount,
  url,
  pageStatus,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const isPageCancelled = pageStatus === PageStatus.Cancelled;

  return (
    <li
      className={classNames(styles.root, {
        [styles.cancelled]: isPageCancelled,
      })}
    >
      {Boolean(url && !isPageCancelled) ? (
        <a
          href={url}
          className={styles.avatar}
          aria-label={typeof title === 'string' ? title : ''}
          tabIndex={0}
        >
          <Avatar avatarSize="auto" picture={image ? image : ''} />
        </a>
      ) : (
        <div className={styles.avatar}>
          <Avatar avatarSize="auto" picture={image ? image : ''} />
        </div>
      )}
      <div className={styles.content}>
        <div className="jg-flex-g1">
          {Boolean(url) && !isPageCancelled ? (
            <h3 className="jg-h5 jg-text-word-break--break-word">
              <a
                href={url}
                className={classNames(styles.link, {
                  [styles.cancelled]: isPageCancelled,
                })}
              >
                {title}
              </a>
            </h3>
          ) : (
            <h3
              className={classNames('jg-h5', 'jg-text-word-break--break-word', {
                [styles.cancelled]: isPageCancelled,
              })}
            >
              {title}
            </h3>
          )}

          {typeof amount === 'number' && currencyCode && (
            <p
              className={classNames('jg-text--brand', 'jg-text-color-branded', {
                [styles.cancelled]: isPageCancelled,
              })}
              data-qa="benefactor-amount"
            >
              {parseCurrency(currencyCode, amount)}
            </p>
          )}
          {isPageCancelled ? (
            <p>{t('common.cancelled')}</p>
          ) : (
            <Trans i18nKey="common.raisedBy" count={supporterCount}>
              raised by{' '}
              <span className="jg-text--regular">
                {{ supporterCount: supporterCount }} supporter
              </span>
            </Trans>
          )}
        </div>
        {typeof totaliserValue === 'number' && !isPageCancelled && (
          <Totaliser
            percent={totaliserValue}
            size="small"
            className={classNames(styles.totaliser, 'jg-pull-right')}
            data-qa="benefactor-totaliser"
          />
        )}
      </div>
    </li>
  );
};

export default BenefactorListItem;
